// Shared domain model — the app's core vocabulary (segments, dataset shapes,
// targeting primitives). Lifted to lib/ so features reference one neutral
// module instead of importing each other for these types (the audience, thread,
// segment, and distribution features all build on this). Framework-agnostic by
// the lib/** layer rule: no React / Mantine / query imports.

export const DatasetType = {
    // Discriminant values match the backend `dataset_type` (StrEnum in
    // services/datasets/domain.py). The backend vocabulary migration renamed
    // `standard`/`sparse` -> `user_attribute` and `custom`/`dense` -> `transaction`.
    USER_ATTRIBUTE: 'user_attribute',
    TRANSACTION: 'transaction',
    // A crossed segment is the intersection of a transaction definition and a
    // user-attribute definition.
    CROSSED: 'crossed',
} as const
export type DatasetType = (typeof DatasetType)[keyof typeof DatasetType]

// Dataset metadata as surfaced in `AgentState.datasets`. Mirrors the shared
// backend `BaseDataset` fields (services/datasets/domain.py); the per-kind
// subclasses add fields the UI doesn't consume, and the backend rejects client
// patches to `datasets`, so only the shared shape is modeled here.
export interface Dataset {
    id: string
    dataset_type: DatasetType
    name: string
    description: string
}

export interface BaseAudienceConfiguration {
    dataset_type: DatasetType
    dataset_id: string
    segment_id: string
}

// --- Targeting primitives (mirror the backend models) ---

// A single field/value condition, e.g. { field: 'brand_name', values: ['NIKE'] }
// (transaction), or { field: 'ab_column_name', values: ['lu_natlinc'] } over a tall
// user-attribute table. Backend `FieldValue` (services/audiences/domain.py).
export interface FieldValue {
    field: string
    values: string[]
}

// Boolean grouping of field conditions (backend `TransactionDefinition`). The
// building block of transaction behaviors. (User attributes use the flat
// `UserGroup` / `UserAttributesDefinition` pair below, not this.)
export interface TransactionDefinition {
    must: FieldValue[]
    should: FieldValue[]
    must_not: FieldValue[]
}

// One transaction behavior pattern (backend `UserTransactionBehavior`). The
// `description` is a human-readable summary of the targeting criteria.
export interface UserTransactionBehavior {
    description: string
    must: TransactionDefinition[]
    should: TransactionDefinition[]
    must_not: TransactionDefinition[]
}

// --- User-attribute targeting primitives ---
// A user-attribute dataset is TALL: each row is one (attribute, value) pair on the
// segment table — e.g. an attribute primary-key column (`ab_column_name`) of
// "parent_status" with a value primary-key column (`attribute_value`) of "likely".
// The attribute side mirrors the transaction side's OUTER two levels, but its inner
// group is FLAT — a single conjunction of conditions on one tall row, with no inner
// must/should/must_not nesting and no per-condition negation (negation is group-level,
// via the definition's `must_not`). Maps to the backend (services/audiences/domain.py):
//   UserGroup                ≈ TransactionDefinition   — a flat AND of conditions
//   UserAttributesDefinition ≈ UserTransactionBehavior — groups of groups

// One group of users (backend `UserGroup`): a flat conjunction of `FieldValue`
// conditions that ALL hold (AND) on a single row of the tall segment table. Each
// condition matches a real column (`field`) against accepted `values`; a group pins
// one attribute by constraining the dataset's primary-key columns (the attribute id
// and its value). Reuses the plain transaction `FieldValue` — the refactor to generic
// FieldValue semantics dropped the old per-condition `negate` (negate a whole group
// via the definition's `must_not`). A multi-value condition is an OR over that
// column's values.
export interface UserGroup {
    conditions: FieldValue[]
}

// Two-level boolean query over user attributes (backend `UserAttributesDefinition`):
// must (AND) / should (OR) / must_not (NOR) of `UserGroup`. Fixed depth (groups of
// conditions, never groups of groups) yet propositionally complete as DNF.
export interface UserAttributesDefinition {
    must: UserGroup[]
    should: UserGroup[]
    must_not: UserGroup[]
}

// User-attribute segment — backend `UserAttributeAudienceConfiguration`.
export interface UserAttributeAudienceConfiguration extends BaseAudienceConfiguration {
    dataset_type: typeof DatasetType.USER_ATTRIBUTE
    // Required (backend `name: str` / `description: str`): the agent must provide
    // both. `name` is the card title, `description` its subtitle.
    name: string
    description: string
    audience_size: number
    users_with_attributes?: UserAttributesDefinition
}

// Transaction segment — backend `TransactionAudienceConfiguration`
// (`users_with_transactions`).
export interface TransactionAudienceConfiguration extends BaseAudienceConfiguration {
    dataset_type: typeof DatasetType.TRANSACTION
    // Required (backend `name: str` / `description: str`): the agent must provide
    // both. `name` is the card title, `description` its subtitle.
    name: string
    description: string
    audience_size: number
    users_with_transactions?: UserTransactionBehavior[]
}

// Crossed segment — backend `CrossedAudienceConfiguration`, which subclasses the
// transaction config: it carries a transaction definition (`users_with_transactions`)
// intersected with a user-attribute definition (`users_with_attributes`). The
// definitions default server-side (empty list / empty definition), so both are
// optional here to match. A cross spans two datasets: the inherited `dataset_id`
// is the transaction side; `users_with_attributes_dataset_id` is the
// user-attribute side (required, like its server counterpart).
export interface CrossedAudienceConfiguration extends BaseAudienceConfiguration {
    dataset_type: typeof DatasetType.CROSSED
    // Required (backend `name: str` / `description: str`): the agent must provide
    // both. `name` is the card title, `description` its subtitle.
    name: string
    description: string
    audience_size: number
    users_with_transactions?: UserTransactionBehavior[]
    users_with_attributes_dataset_id: string
    // Plain-English summary of the attribute side of the cross (the
    // `users_with_attributes` selection). The transaction side describes itself
    // per `UserTransactionBehavior.description`; the attribute side has no such
    // per-item note, so the cross carries one. Required (the agent provides it).
    users_with_attributes_description: string
    users_with_attributes?: UserAttributesDefinition
}

export type AudienceConfiguration =
    | TransactionAudienceConfiguration
    | UserAttributeAudienceConfiguration
    | CrossedAudienceConfiguration

// A thread surfaced as an Audience (v1: one Audience per thread = its cart of
// segments). View-model the inventory screen reads, mapped from the thread list
// in `src/hooks/useAudienceInventory.ts`.
export interface AudienceThread {
    threadId: string
    name: string
    lastUpdated: string
    segments: AudienceConfiguration[]
    // The thread's available datasets (from AgentState.datasets). Carried so the
    // shared SegmentDetail can offer the user-attribute datasets as expansion
    // (lookalike) modeling targets, even when opened from the inventory screens.
    datasets: Dataset[]
}
